import React, { useState, useEffect } from 'react';
import { contactAPI } from '../../utils/api';
import '../../styles/AdminTable.scss';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await contactAPI.getAll();
      
      // Backend returns { data: [...], total: X, message: "..." }
      const responseData = response.data || response;
      const contactsArray = Array.isArray(responseData.data) ? responseData.data : [];
      
      setContacts(contactsArray);
      console.log('Loaded contacts:', contactsArray);
    } catch (err) {
      setError('Failed to load contacts');
      console.error('Error:', err);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await contactAPI.markAsRead(id);
      setContacts(contacts.map(c => c.id === id ? { ...c, is_read: true } : c));
      if (selectedContact?.id === id) {
        setSelectedContact({ ...selectedContact, is_read: true });
      }
    } catch (err) {
      setError('Failed to update contact');
      console.error('Error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await contactAPI.delete(id);
        setContacts(contacts.filter(c => c.id !== id));
        setSelectedContact(null);
      } catch (err) {
        setError('Failed to delete contact');
        console.error('Error:', err);
      }
    }
  };

  const unreadCount = contacts.filter(c => !c.is_read).length;

  return (
    <div className="admin-page">
        <div className="page-header">
          <h1>Contact Messages</h1>
          <p>
            {unreadCount > 0 ? (
              <span className="unread-badge">{unreadCount} unread</span>
            ) : (
              'All messages read'
            )}
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className={`content-wrapper ${selectedContact ? 'has-detail' : ''}`}>
          <div className="table-container">
            {loading ? (
              <p>Loading...</p>
            ) : contacts.length === 0 ? (
              <p>No contact messages</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr key={contact.id} className={!contact.is_read ? 'unread' : ''}>
                      <td>{contact.name}</td>
                      <td>{contact.email}</td>
                      <td>{contact.subject}</td>
                      <td>
                        <span className={`badge ${contact.is_read ? 'read' : 'unread'}`}>
                          {contact.is_read ? 'Read' : 'Unread'}
                        </span>
                      </td>
                      <td>{new Date(contact.created_at).toLocaleDateString()}</td>
                      <td className="actions">
                        <button
                          className="btn-view"
                          onClick={() => setSelectedContact(contact)}
                          title="View Message"
                        >
                          👁️
                        </button>
                        {!contact.is_read && (
                          <button
                            className="btn-success"
                            onClick={() => handleMarkAsRead(contact.id)}
                            title="Mark as Read"
                          >
                            ✓
                          </button>
                        )}
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(contact.id)}
                          title="Delete Message"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {selectedContact && (
            <div className="detail-panel">
              <div className="panel-header">
                <h2>Message Details</h2>
                <button 
                  className="btn-close" 
                  onClick={() => setSelectedContact(null)}
                >
                  ✕
                </button>
              </div>
              <div className="panel-content">
                <div className="detail-row">
                  <strong>Name:</strong>
                  <span>{selectedContact.name}</span>
                </div>
                <div className="detail-row">
                  <strong>Email:</strong>
                  <span>
                    <a href={`mailto:${selectedContact.email}`}>
                      {selectedContact.email}
                    </a>
                  </span>
                </div>
                {selectedContact.phone && (
                  <div className="detail-row">
                    <strong>Phone:</strong>
                    <span>{selectedContact.phone}</span>
                  </div>
                )}
                <div className="detail-row">
                  <strong>Subject:</strong>
                  <span>{selectedContact.subject}</span>
                </div>
                <div className="detail-row">
                  <strong>Status:</strong>
                  <span className={`badge ${selectedContact.is_read ? 'read' : 'unread'}`}>
                    {selectedContact.is_read ? 'Read' : 'Unread'}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Message:</strong>
                  <p className="message-content">{selectedContact.message}</p>
                </div>
                <div className="detail-row">
                  <strong>Received:</strong>
                  <span>{new Date(selectedContact.created_at).toLocaleString()}</span>
                </div>

                <div className="panel-actions">
                  {!selectedContact.is_read && (
                    <button
                      className="btn-success"
                      onClick={() => handleMarkAsRead(selectedContact.id)}
                    >
                      ✓ Mark as Read
                    </button>
                  )}
                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(selectedContact.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
  );
};

export default AdminContacts;
